import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box, CircularProgress,
  Alert, Chip
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import { API_CONFIG } from '../config';
import { useLanguage } from '../i18n/LanguageContext';

interface BillingStatus {
  subscription_status: string;
  has_subscription: boolean;
}

const Billing: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (searchParams.get('success') === 'true') setNotice(t.paymentSuccessful);
    if (searchParams.get('canceled') === 'true') setNotice(t.paymentCanceled);
    api.get(API_CONFIG.endpoints.billing.status)
      .then(r => setStatus(r.data))
      .catch(() => setError(t.subscribeFailed))
      .finally(() => setLoading(false));
  }, [searchParams, t.paymentSuccessful, t.paymentCanceled, t.subscribeFailed]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    setError('');
    try {
      const resp = await api.post(API_CONFIG.endpoints.billing.createCheckout);
      window.location.href = resp.data.checkout_url;
    } catch {
      setError(t.subscribeFailed);
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        {t.billingTitle}
      </Typography>

      {notice && (
        <Alert
          severity={searchParams.get('success') === 'true' ? 'success' : 'info'}
          sx={{ mb: 2 }}
          onClose={() => setNotice('')}
        >
          {notice}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {status?.has_subscription ? (
            <>
              <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h6" gutterBottom>{t.activeSubscription}</Typography>
              <Chip
                label={status.subscription_status}
                color="success"
                sx={{ mb: 2 }}
              />
            </>
          ) : (
            <>
              <PaymentIcon color="action" sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h6" gutterBottom>{t.noSubscription}</Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubscribe}
                disabled={subscribing}
                sx={{ mt: 2 }}
              >
                {subscribing ? <CircularProgress size={22} color="inherit" /> : t.subscribeNow}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Billing;
